import { memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import { gsap, prefersReducedMotion, useGSAP } from "@/animations";
import { AtlasPanel, Button, HeroParticleField, MetricStrip, Reveal, SdgBadge, SectionHeading, Seo, WaterHeroScene } from "@/components";
import { Container } from "@/layouts";
import { SDGS } from "@/data";
import { ROUTES, normalizeLanguage, useLocalePath } from "@/navigations";
import { absoluteUrl, breadcrumbJsonLd, researchProjectJsonLd } from "@/utils/seo";

import shared from "@/styles/pages.module.css";
import styles from "./styles.module.css";

const HomePage = memo(() => {
  const { t } = useTranslation("HomePage");
  const { t: tSeo } = useTranslation("Seo");
  const { t: tc, i18n } = useTranslation("components");
  const lang = normalizeLanguage(i18n.language);
  const lp = useLocalePath();
  const heroRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!heroRef.current) return;
      const targets = heroRef.current.querySelectorAll("[data-hero]");
      if (prefersReducedMotion()) {
        gsap.set(targets, { opacity: 1, y: 0 });
        return;
      }
      gsap.from(targets, {
        opacity: 0,
        y: 28,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.12,
      });
    },
    { scope: heroRef },
  );

  const statusItems = t("status.items", { returnObjects: true }) as { value: string; label: string; note: string }[];
  const panelKeys = ["platform", "sense", "viz"] as const;

  return (
    <>
      <Seo
        title={tSeo("home.title")}
        description={tSeo("home.description")}
        path={ROUTES.HOME}
        jsonLd={[
          researchProjectJsonLd(tSeo("home.description"), absoluteUrl(ROUTES.HOME, lang)),
          breadcrumbJsonLd([{ name: tc("nav.home"), path: ROUTES.HOME }], lang),
        ]}
      />

      <section className={styles.hero} ref={heroRef}>
        <div className={styles.heroGlow} aria-hidden="true" />
        <HeroParticleField />
        <Container className={styles.heroInner}>
          <div className={styles.heroText}>
            <span className="eyebrow" data-hero>
              {t("hero.eyebrow")}
            </span>
            <h1 className={styles.heroTitle} data-hero>
              {t("hero.title")}
            </h1>
            <p className={styles.heroSubtitle} data-hero>
              {t("hero.subtitle")}
            </p>
            <div className={styles.heroActions} data-hero>
              <Button to={lp(ROUTES.GET_INVOLVED)} variant="primary">
                {t("hero.ctaPrimary")}
                <ArrowRight size={18} />
              </Button>
              <Button to={lp(ROUTES.ABOUT)} variant="secondary">
                {t("hero.ctaSecondary")}
              </Button>
            </div>
            <p className={styles.heroLegend} data-hero>
              {t("hero.legend")}
            </p>
          </div>
          <div className={styles.heroScene} data-hero>
            <WaterHeroScene />
          </div>
        </Container>
      </section>

      <section className={styles.metricsSection}>
        <Container>
          <MetricStrip items={statusItems} />
        </Container>
      </section>

      <section className={`${shared.section} ${shared.fieldbook}`}>
        <Container>
          <Reveal>
            <SectionHeading title={t("identity.title")} lead={t("identity.body")} />
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.atlas}`}>
        <Container>
          <SectionHeading title={t("pillars.title")} lead={t("pillars.lead")} />
          <Reveal className={`${styles.atlasGrid} ${shared.stackGap}`}>
            {panelKeys.map((key, index) => (
              <AtlasPanel
                key={key}
                kicker={t(`pillars.panels.${key}.kicker`)}
                title={t(`pillars.panels.${key}.title`)}
                variant={index === 1 ? "light" : "dark"}
              >
                {t(`pillars.panels.${key}.body`)}
              </AtlasPanel>
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.fieldbook}`}>
        <Container>
          <Reveal className={shared.asymmetric}>
            <div>
              <SectionHeading title={t("roadmap.title")} lead={t("roadmap.lead")} />
              <div className={styles.contextAction} data-reveal>
                <Button to={lp(ROUTES.ABOUT)} variant="secondary">
                  {t("roadmap.cta")}
                  <ArrowRight size={18} />
                </Button>
              </div>
            </div>
            <div>
              <SectionHeading title={t("context.title")} lead={t("context.body")} />
              <div className={styles.contextAction} data-reveal>
                <Button to={lp(ROUTES.SCIENCE)} variant="secondary">
                  {t("context.cta")}
                  <ArrowRight size={18} />
                </Button>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <SectionHeading title={t("sdgs.title")} />
          <Reveal className={`${styles.sdgGrid} ${shared.stackGap}`}>
            {SDGS.map((sdg) => (
              <SdgBadge key={sdg.id} number={sdg.number} label={t(`sdgs.items.${sdg.id}`)} />
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.invert}`}>
        <Container className={styles.ctaInner}>
          <Reveal>
            <h2 className={styles.ctaTitle} data-reveal>
              {t("cta.title")}
            </h2>
            <p className={styles.ctaBody} data-reveal>
              {t("cta.body")}
            </p>
            <div data-reveal>
              <Button to={lp(ROUTES.GET_INVOLVED)} variant="primary">
                {t("cta.button")}
                <ArrowRight size={18} />
              </Button>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
});

HomePage.displayName = "HomePage";
export default HomePage;
